import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SiteService } from 'src/app/shared/services/site.service';
import { NoDataComponent } from 'src/app/shared/components/no-data/no-data.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { ModificationButtonsComponent } from 'src/app/shared/components/modification-buttons/modification-buttons.component';
import { SiteSettings } from '../../shared/models/siteSettings';
import {
  COL_COVERAGE_MAX,
  COL_COVERAGE_MIN,
  COL_FROZEN_PERIOD,
  COL_ORDER_MULTIPLE,
  COL_QTY_MIN,
  IS_DATA_LINE,
  MAX_MONTH_QTY
} from 'src/app/shared/constants/SiteStockConstants';
import { MIN_LOADING_TIME_MS } from 'src/app/shared/utils/constants';
import { forkJoin, Subscription, timer } from 'rxjs';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { TranslateService } from '@ngx-translate/core';
import { NoScrollDirective } from '../../directives/nx-no-scroll.directive';
import { ContextService } from '../../shared/services/context.service';
import { IModif } from '../../shared/models/IModif';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';


@Component({
  selector: 'app-site-settings',
  imports: [SharedModule, ModificationButtonsComponent, NoScrollDirective, NoDataComponent, NgxMaskDirective],
  templateUrl: './site-settings.component.html',
  styleUrls: ['./site-settings.component.scss'],
  providers: [provideNgxMask()]
})
export class SiteSettingsComponent implements OnInit, OnDestroy, IModif {
  siteId: number | undefined;
  sizeSettingsForm: FormArray;
  originalData: SiteSettings = undefined;
  isLoading = false;
  hasPendingModifications: boolean = false;

  readonly digitMask = '0*';
  readonly digitPatterns = { '0': { pattern: /\d/ } };

  //Constantes pour utilisation dans le template
  COL_FROZEN_PERIOD = COL_FROZEN_PERIOD;
  COL_COVERAGE_MIN = COL_COVERAGE_MIN;
  COL_COVERAGE_MAX = COL_COVERAGE_MAX;
  COL_QTY_MIN = COL_QTY_MIN;
  COL_ORDER_MULTIPLE = COL_ORDER_MULTIPLE;
  IS_DATA_LINE: string = IS_DATA_LINE;

  private mineContextSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private siteService: SiteService,
    private alertService: AlertService,
    private translate: TranslateService,
    private contextService: ContextService
  ) {
    this.contextService.register(this)
    this.sizeSettingsForm = this.formBuilder.array([]);
  }

  getModifStatus(): boolean {
    return this.sizeSettingsForm.dirty
  }

  ngOnInit(): void {
    this.mineContextSub = this.contextService.siteId$.subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.loadSizeSettings();
      }
    });
  }

  /**
   * Loads the size settings for the current site.
   */
  loadSizeSettings(): void {
    this.isLoading = true;
    const load$ = this.siteService.getSizeSettings(this.siteId);
    const delay$ = timer(MIN_LOADING_TIME_MS);
    forkJoin([load$, delay$]).subscribe({
      next: ([data]) => {
        this.originalData = data;
        this.sizeSettingsForm = this.formBuilder.array(
          data.stockSettings.map((item) => {
            return this.formBuilder.group({
              isDataLine: true,
              size: [{ value: item.size, disabled: true }],
              [COL_FROZEN_PERIOD]: [item.frozenPeriod, Validators.required],
              [COL_COVERAGE_MIN]: [item.coverageMin, Validators.required],
              [COL_COVERAGE_MAX]: [item.coverageMax, Validators.required],
              [COL_QTY_MIN]: [item.quantityMin, [Validators.required, Validators.max(9999)]],
              [COL_ORDER_MULTIPLE]: [item.orderMultiple, [Validators.required, Validators.max(9999)]]
            });
          })
        );

        // Add row in table in order to update all sizes at once
        this.sizeSettingsForm.insert(
          0,
          this.formBuilder.group({
            isDataLine: false,
            size: [{ value: 'All Sizes', disabled: true }],
            [COL_FROZEN_PERIOD]: [{ value: undefined }],
            [COL_COVERAGE_MIN]: [{ value: undefined }],
            [COL_COVERAGE_MAX]: [{ value: undefined }],
            [COL_QTY_MIN]: [{ value: undefined }],
            [COL_ORDER_MULTIPLE]: [{ value: undefined }]
          })
        );

        this.isLoading = false;
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          title: this.translate.instant('sites.siteSettings.stockLoadingError'),
          content: `${this.translate.instant('sites.siteSettings.stockLoadingErrorMsg')}`
        } as Alert);
        this.sizeSettingsForm.markAsPristine();
        this.isLoading = false;
      }
    });
  }

  get sizeSettingsControls() {
    return this.sizeSettingsForm.controls as FormGroup[];
  }

  /**
   * Saves the modifications made to the size settings.
   */
  saveModifications(): void {
    this.isLoading = true;
    const modified = this.sizeSettingsForm
      .getRawValue()
      .filter((row) => row.isDataLine)
      .map(({ isDataLine, ...rest }) => rest);
    const save$ = this.siteService.saveSizeSettings(this.siteId, modified);

    const delay$ = timer(MIN_LOADING_TIME_MS);
    forkJoin([save$, delay$]).subscribe({
      next: () => {
        this.sizeSettingsForm.markAsPristine();
        this.isLoading = false;

        //Reset all input size "All Sizes"
        this.sizeSettingsControls.forEach((group) => {
          if (!group.get(IS_DATA_LINE).value) {
            group.patchValue({
              [this.COL_FROZEN_PERIOD]: undefined,
              [this.COL_COVERAGE_MIN]: undefined,
              [this.COL_COVERAGE_MAX]: undefined,
              [this.COL_QTY_MIN]: undefined,
              [this.COL_ORDER_MULTIPLE]: undefined
            });
          }
        });

        this.alertService.showAlert({
          type: AlertType.success,
          title: this.translate.instant('sites.siteSettings.stockUpdated'),
          content: this.translate.instant('sites.siteSettings.stockUpdateSuccess')
        } as Alert);
      },
      error: (err) => {
        let errorMsg: string | undefined = undefined;
        if (err.error && err.error.message) {
          errorMsg = `(${err.error.message})`;
        }
        this.alertService.showAlert({
          type: AlertType.critical,
          title: this.translate.instant('sites.siteSettings.stockUpdateError'),
          content: this.translate.instant('sites.siteSettings.stockUpdateErrorMsg')
        } as Alert);
        this.isLoading = false;
      }
    });
  }

  /**
   * Resets the modifications made to the size settings.
   */
  resetModifications(): void {
    this.loadSizeSettings();
    this.sizeSettingsForm.markAsPristine();
  }

  /**
   * Handles the update of a whole column when the user changes the value in the header row.
   * @param column
   * @param event
   * @protected
   */
  protected updateColumns(column: string, event: Event): void {
    let hasModifications = false;
    const input = event.target as HTMLInputElement;
    const valueStr = input.value;

    if (!valueStr || !valueStr.trim().length) return;

    let value = this.limitValue(column, Number(valueStr));

    //Update Input Value
    input.value = String(value);

    hasModifications = this.propagateValueToColumn(column, value, false, false) || hasModifications;

    if (column === this.COL_COVERAGE_MIN) {
      hasModifications = this.propagateValueToColumn(this.COL_COVERAGE_MAX, value, true, false) || hasModifications;
    }
    else if (column === this.COL_COVERAGE_MAX) {
      hasModifications = this.propagateValueToColumn(this.COL_COVERAGE_MIN, value, false, true) || hasModifications;
    }

    if (hasModifications) {
      this.sizeSettingsForm.markAsDirty();
    }
  }

  /**
   * Limits the value based on the column type and predefined maximum and minimum values.
   * @param column
   * @param value
   * @private
   */
  private limitValue(column: string, value: number): number {
    const isCoverageOrFrozen = [this.COL_COVERAGE_MIN, this.COL_COVERAGE_MAX, this.COL_FROZEN_PERIOD].includes(column);
    const isQtyOrMultiple = [this.COL_QTY_MIN, this.COL_ORDER_MULTIPLE].includes(column);

    //Superior Limit
    if (isCoverageOrFrozen && value > MAX_MONTH_QTY) {
      return MAX_MONTH_QTY;
    }
    if (isQtyOrMultiple && value > 9999) {
      return 9999;
    }

    //Inferior Limit
    if (value <= 0) {
      if ([this.COL_COVERAGE_MIN, this.COL_COVERAGE_MAX, this.COL_QTY_MIN].includes(column)) {
        return 0;
      }
      return 1;
    }

    return value;
  }

  /**
   * Callback on change of frozen period to validate the value
   * @param control
   */
  onFrozenPeriodChange(control: FormControl) {
    if (control.value > MAX_MONTH_QTY) {
      control.setValue(MAX_MONTH_QTY);
    } else if (control.value <= 0) {
      control.setValue(1);
    }
  }

  /**
   * Callback on change of Coverage Min to validate the value
   */
  onCoverageMinChange(group: FormGroup) {
    let value: number = group.get(COL_COVERAGE_MIN).value;

    if (value > MAX_MONTH_QTY) {
      group.get(COL_COVERAGE_MIN).setValue(MAX_MONTH_QTY);
      value = MAX_MONTH_QTY;
    } else if (value <= 0) {
      group.get(COL_COVERAGE_MIN).setValue(0);
      value = 0;
    }

    if (value >= group.get(COL_COVERAGE_MAX).value) {
      group.get(COL_COVERAGE_MAX).setValue(value);
    }
  }

  /**
   * Callback on change of Coverage Max to validate the value
   */
  onCoverageMaxChange(group: FormGroup) {
    let value: number = group.get(COL_COVERAGE_MAX).value;

    if (value > MAX_MONTH_QTY) {
      group.get(COL_COVERAGE_MAX).setValue(MAX_MONTH_QTY);
      value = MAX_MONTH_QTY;
    } else if (value <= 0) {
      group.get(COL_COVERAGE_MAX).setValue(0);
      value = 0;
    }

    if (value <= group.get(COL_COVERAGE_MIN).value) {
      group.get(COL_COVERAGE_MIN).setValue(value);
    }
  }

  /**
   * Callback on change of Qty min
   */
  onQtyMinChange(control: FormControl) {
    if (control.value <= 0) {
      control.setValue(0);
    }
    else if (control.value > 9999) {
      control.setValue(9999)
    }
  }

  /**
   * Callback on change of orderMultiple
   */
  onOrderMultipleChange(control: FormControl) {
    if (control.value <= 1) {
      control.setValue(1);
    }
    else if (control.value > 9999) {
      control.setValue(9999)
    }
  }

  /**
   * Propagates a value to all rows in a specified column, with options to limit the update to only inferior or superior values.
   * @param column
   * @param value
   * @param updateInferiorOnly
   * @param updateSuperiorOnly
   */
  propagateValueToColumn(column: string, value: number, updateInferiorOnly: boolean, updateSuperiorOnly: boolean = false): boolean {
    let hasModifications:boolean = false;
    this.sizeSettingsControls.forEach((group) => {
      if (group.get(column)) {
        if (
          (!updateInferiorOnly && !updateSuperiorOnly) ||
          (updateInferiorOnly && group.get(column).value < value) ||
          (updateSuperiorOnly && group.get(column).value > value)
        ) {
          group.get(column).setValue(value);
          hasModifications = true;
        }
      }
    });
    return hasModifications;
  }

  ngOnDestroy(): void {
    if (this.mineContextSub) {
      this.mineContextSub.unsubscribe();
    }
    this.contextService.unRegister(this)
  }
}
